// components/PropertyIssues/IssueThread.jsx
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  TextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Link,
  Chip,
  Tooltip,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import RefreshIcon from "@mui/icons-material/Refresh";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ImageIcon from "@mui/icons-material/Image";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import {
  addDoc,
  arrayUnion,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { auth, storage } from "../../firebase";
import PropertyContext from "../../contexts/PropertyContext";
import { propertyCollection, propertyDoc } from "../../utils/firestorePaths";

export default function IssueThread({ issueId, clientId, propertyId, height = 420, userInfo }) {
  const { selectedProperty, selectedClient } = useContext(PropertyContext);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [quoteDialogOpen, setQuoteDialogOpen] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const bottomRef = useRef(null);
  const fileInputRef = useRef(null);

  // Use propertyId prop if provided, otherwise fall back to selectedProperty from context
  const effectivePropertyId = propertyId || selectedProperty;
  const effectiveClientId = clientId || selectedClient;

  const msgsRef = useMemo(
    () =>
      propertyCollection(
        effectiveClientId,
        effectivePropertyId,
        "propertyIssues",
        issueId,
        "messages"
      ),
    [effectiveClientId, effectivePropertyId, issueId]
  );

  useEffect(() => {
    const q = query(msgsRef, orderBy("createdAt", "asc"));
    const unsub = onSnapshot(q, (snap) => {
      setMessages(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, [msgsRef]);

  // Mark last 50 as read when thread loads/updates
  useEffect(() => {
    const uid = auth.currentUser?.uid;
    if (!uid || !messages.length) return;
    messages.slice(-50).forEach(async (m) => {
      const mref = propertyDoc(
        effectiveClientId,
        effectivePropertyId,
        "propertyIssues",
        issueId,
        "messages",
        m.id
      );
      try {
        await updateDoc(mref, { readBy: arrayUnion(uid) });
      } catch {}
    });
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, issueId, effectiveClientId, effectivePropertyId]);

  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const isPdf = file.type === "application/pdf";

    if (isPdf) {
      // Ask if it's a quote
      setPendingFile(file);
      setQuoteDialogOpen(true);
    } else {
      // Upload directly
      uploadFile(file, false);
    }

    // Reset input
    event.target.value = "";
  };

  const uploadFile = async (file, isQuote = false) => {
    setIsUploading(true);
    try {
      const date = new Date().toISOString().split("T")[0];
      const storageRef = ref(
        storage,
        `property-issues/${effectivePropertyId}/${issueId}/messages/${Date.now()}-${file.name}`
      );

      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);

      const uid = auth.currentUser?.uid || "anon";
      const displayName = userInfo?.fullName || "Unknown";

      // Add message with attachment
      await addDoc(msgsRef, {
        text: text.trim() || (isQuote ? "Quote uploaded" : "Attachment uploaded"),
        createdAt: serverTimestamp(),
        createdBy: { uid, displayName },
        readBy: [uid],
        attachment: {
          name: file.name,
          url,
          type: file.type,
          size: file.size,
          isQuote,
        },
      });

      // Also update the issue's files array
      const issueRef = propertyDoc(
        effectiveClientId,
        effectivePropertyId,
        "propertyIssues",
        issueId
      );
      await updateDoc(issueRef, {
        files: arrayUnion({
          name: file.name,
          url,
          type: file.type,
          size: file.size,
          isQuote,
        }),
      });

      setText("");
    } catch (error) {
      console.error("Error uploading file:", error);
      alert("Error uploading file. Please try again.");
    } finally {
      setIsUploading(false);
    }
  };

  const handleQuoteResponse = (isQuote) => {
    setQuoteDialogOpen(false);
    if (pendingFile) {
      uploadFile(pendingFile, isQuote);
      setPendingFile(null);
    }
  };

  const send = async () => {
    if (!text.trim()) return;
    const uid = auth.currentUser?.uid || "anon";
    const displayName = userInfo?.fullName || "Unknown";
    await addDoc(msgsRef, {
      text: text.trim(),
      createdAt: serverTimestamp(),
      createdBy: { uid, displayName },
      readBy: [uid],
    });
    setText("");
  };

  const getFileIcon = (type, isQuote) => {
    if (isQuote) return PictureAsPdfIcon;
    if (type?.includes("pdf")) return PictureAsPdfIcon;
    if (type?.includes("image")) return ImageIcon;
    if (type?.includes("video")) return VideoLibraryIcon;
    return InsertDriveFileIcon;
  };

  return (
    <Box
      sx={{
        border: "1px solid rgba(0,0,0,0.12)",
        borderRadius: 1,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          p: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="subtitle2">Issue Thread</Typography>
        <IconButton
          size="small"
          onClick={() =>
            bottomRef.current?.scrollIntoView({ behavior: "smooth" })
          }
        >
          <RefreshIcon fontSize="small" />
        </IconButton>
      </Box>
      <Divider />
      <Box sx={{ p: 1.5, overflowY: "auto", height }}>
        <List dense>
          {messages.map((m) => {
            const FileIcon = m.attachment ? getFileIcon(m.attachment.type, m.attachment.isQuote) : null;

            return (
              <ListItem key={m.id} alignItems="flex-start" sx={{ px: 0 }}>
                <ListItemText
                  primary={m.createdBy?.displayName || "Unknown"}
                  secondary={
                    <>
                      <Typography component="span" variant="body2">
                        {m.text}
                      </Typography>
                      {m.attachment && (
                        <Box sx={{ mt: 0.5 }}>
                          <Tooltip title={m.attachment.name}>
                            <Chip
                              icon={FileIcon ? <FileIcon /> : <InsertDriveFileIcon />}
                              label={m.attachment.isQuote ? "Quote" : m.attachment.name}
                              size="small"
                              component="a"
                              href={m.attachment.url}
                              target="_blank"
                              clickable
                              color={m.attachment.isQuote ? "primary" : "default"}
                              sx={{ maxWidth: "200px" }}
                            />
                          </Tooltip>
                        </Box>
                      )}
                    </>
                  }
                />
              </ListItem>
            );
          })}
        </List>
        <div ref={bottomRef} />
      </Box>
      <Divider />
      <Box sx={{ p: 1.5, display: "flex", gap: 1 }}>
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: "none" }}
          accept="image/*,video/*,.pdf"
          onChange={handleFileSelect}
        />
        <IconButton
          size="small"
          onClick={() => fileInputRef.current?.click()}
          disabled={isUploading}
        >
          <AttachFileIcon />
        </IconButton>
        <TextField
          fullWidth
          size="small"
          placeholder="Message…"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !isUploading) send();
          }}
          disabled={isUploading}
        />
        <Button
          variant="contained"
          onClick={send}
          endIcon={<SendIcon />}
          disabled={isUploading}
        >
          Send
        </Button>
      </Box>

      {/* Quote Confirmation Dialog */}
      <Dialog open={quoteDialogOpen} onClose={() => setQuoteDialogOpen(false)}>
        <DialogTitle>Is this a quote?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You've selected a PDF file. Is this a quote from a vendor? Quotes will be labeled specially in the issue.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleQuoteResponse(false)} color="primary">
            No, Regular PDF
          </Button>
          <Button onClick={() => handleQuoteResponse(true)} color="primary" variant="contained">
            Yes, It's a Quote
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
